import React, { FC } from 'react';

import slice from 'lodash/slice';
import compact from 'lodash/compact';
import { CAMPAIGN_PRODUCTS, MULTI_LANGUAGE_SHOP, useFeature } from '../../utils/togglz';

import { BadgesContainer, BadgesContainerProps } from './BadgesContainer';
import { buildInfoBadgeLinks } from './utils-badges';
import { CampaignBadge, InfoBadge, StandingOrderBadge, WarningBadge } from './Badge';
import { useLanguage } from '../../../contexts/language';
import { useCampaignContext } from '../../../contexts/campaign';
import { useTranslation } from 'react-i18next';
import { useAgeCategorySearchParam } from '../../utils/useAgeCategorySearchParam';

type Props = {
    readonly loanFormatKey?: string | null;
    readonly product: any;
    readonly showAll?: boolean;
    readonly hideWarningBadges?: boolean;
    readonly shopLanguage: string | undefined;
    readonly closeSuggestions?: () => void;
};

type OwnProps = {
    readonly layout: BadgesContainerProps['layout'];
};

const Badges: FC<Props & OwnProps> = props => {
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();
    const {
        layout,
        loanFormatKey,
        product: { productClass, badges: productBadges = {}, campaignProductId },
        hideWarningBadges,
        showAll,
        shopLanguage = 'all'
    } = props;

    const language = useLanguage();
    const { preventCampaignBadge } = useCampaignContext();
    const isCampaignsEnabled = useFeature(CAMPAIGN_PRODUCTS);

    const { infoBadges, premiumBadges, warningBadges } = productBadges;
    const infoBadgesWithLinks = buildInfoBadgeLinks({
        productClass,
        infoBadges,
        loanFormatKey,
        language,
        isMultiLanguageShopEnabled,
        shopLanguage,
        ageCategory: ageCategorySearchParam
    });
    const productWarningBadges = Array.isArray(warningBadges) && !hideWarningBadges ? warningBadges : [];

    const showCampaignBadge = !preventCampaignBadge && !!campaignProductId && isCampaignsEnabled;

    const infoBadgeContainer = infoBadgesWithLinks.map(infoBadge => ({
        title: infoBadge.title,
        link: infoBadge.link,
        badgeComponent: InfoBadge,
        badgeKey: infoBadge.key
    }));
    const warningBadgeContainer = productWarningBadges.map(productWarningBadge => ({
        title: productWarningBadge.title,
        badgeKey: productWarningBadge.key,
        badgeComponent: WarningBadge
    }));

    const campaignProductAvailableBadge = showCampaignBadge
        ? {
              link: campaignProductId
                  ? `/products/${campaignProductId}`
                  : isMultiLanguageShopEnabled
                  ? `/shop/${shopLanguage ?? 'all'}/unlimited/campaigns/all`
                  : '/unlimited/campaigns/all',
              title: t('shop.campaigns.available'),
              badgeComponent: CampaignBadge
          }
        : null;
    const productStandingOrders = Array.isArray(premiumBadges) ? premiumBadges : [];
    const standingOrderBadges = productStandingOrders.map(productStandingOrder => ({
        title: productStandingOrder.title,
        link: productStandingOrder.link,
        badgeComponent: StandingOrderBadge,
        badgeKey: productStandingOrder.key
    }));

    const allBadges = compact([
        campaignProductAvailableBadge,
        ...standingOrderBadges,
        ...warningBadgeContainer,
        ...infoBadgeContainer
    ]);
    const badges = showAll ? slice(allBadges, 0, 6) : slice(allBadges, 0, 3);
    return <BadgesContainer {...props} layout={layout} badges={badges} />;
};

export const TileBadges: FC<Props> = props => <Badges {...props} layout="tile" />;
export const ListBadges: FC<Props> = props => <Badges {...props} layout="list" showAll={true} />;

export const BaseBadgeComponents = {
    TileBadgesComponent: TileBadges,
    ListBadgesComponent: ListBadges
};
